// Section 4 ("Conclusies / Aanbevelingen") of the Full Physical Report — a small, defensible
// set of auto-generated bullet lines from plain TD/HMLD comparisons, not a workload-risk
// scoring engine (no EWMA/risk-zone analysis here, per confirmed scope). Explicitly a first
// version — expect the wording/thresholds to be revisited once real report output exists.

const f0 = n => (n == null || isNaN(n) ? '—' : Math.round(n).toLocaleString());

function average(rows, key) {
  const vals = rows.map(r => r[key]).filter(v => v != null && !isNaN(v));
  return vals.length ? vals.reduce((s, v) => s + v, 0) / vals.length : null;
}

// rows sorted oldest→newest, split into first/second half by count (not by date span) — simple
// and robust to irregular match spacing.
function trendDirection(rowsAscending, key) {
  const n = rowsAscending.length;
  if (n < 2) return null;
  const mid = Math.ceil(n / 2);
  const first = average(rowsAscending.slice(0, mid), key);
  const second = average(rowsAscending.slice(mid), key);
  if (first == null || second == null || first === 0) return null;
  const pctChange = ((second - first) / first) * 100;
  if (Math.abs(pctChange) < 5) return { direction: 'stable', pctChange };
  return { direction: pctChange > 0 ? 'increasing' : 'decreasing', pctChange };
}

const METRIC_LABEL = { td: 'Total Distance', hmld: 'HMLD' };
const PCT_KEY = { td: 'tdPct', hmld: 'hmldPct' };

/**
 * playerRows: computeMatchRows() output for the selected player, filtered to the selected matches.
 * squadRows: computeTeamMatchRows() output, filtered to the same selected matches.
 * Returns an ordered array of short bullet strings.
 */
export function generateConclusions({ playerRows = [], squadRows = [] } = {}) {
  const bullets = [];
  if (!playerRows.length) {
    bullets.push('No match data available for the selected matches.');
    return bullets;
  }

  const rowsAscending = [...playerRows].sort((a, b) => (a.date || '').localeCompare(b.date || ''));

  // ── Squad comparison bullets ──────────────────────────────────────────────
  ['td', 'hmld'].forEach(key => {
    const playerAvg = average(playerRows, key);
    const squadAvg = average(squadRows, key);
    if (playerAvg == null || squadAvg == null || squadAvg === 0) return;
    const diffPct = ((playerAvg - squadAvg) / squadAvg) * 100;
    const label = METRIC_LABEL[key];
    if (Math.abs(diffPct) < 5) {
      bullets.push(`${label} per match (${f0(playerAvg)}) is in line with the squad average (${f0(squadAvg)}).`);
    } else {
      const dir = diffPct > 0 ? 'above' : 'below';
      bullets.push(`${label} per match (${f0(playerAvg)}) is ${Math.abs(Math.round(diffPct))}% ${dir} the squad average (${f0(squadAvg)}).`);
    }
  });

  // ── Positional target bullets — average %-of-target already computed per row (position's
  // MD scenario target, normalized to minutes played), same figure shown in the TD%/HMLD%
  // table columns. Skipped when no scenario/target could be resolved for any selected match.
  ['td', 'hmld'].forEach(key => {
    const pctAvg = average(playerRows, PCT_KEY[key]);
    if (pctAvg == null) return;
    const label = METRIC_LABEL[key];
    const rounded = Math.round(pctAvg);
    if (Math.abs(rounded - 100) < 5) {
      bullets.push(`${label} is averaging ${rounded}% of his positional target across selected matches — right on target.`);
    } else {
      const dir = rounded > 100 ? 'above' : 'below';
      bullets.push(`${label} is averaging ${rounded}% of his positional target across selected matches — ${dir} target.`);
    }
  });

  // ── Trend bullets ──────────────────────────────────────────────────────────
  ['td', 'hmld'].forEach(key => {
    const trend = trendDirection(rowsAscending, key);
    if (!trend) return;
    const label = METRIC_LABEL[key];
    if (trend.direction === 'stable') {
      bullets.push(`${label} has stayed stable across the selected matches.`);
    } else {
      bullets.push(`${label} is ${trend.direction} across the selected matches (${trend.direction === 'increasing' ? '+' : ''}${Math.round(trend.pctChange)}% from first half to second half of the window).`);
    }
  });

  return bullets;
}
